import type { ReactNode } from 'react';

import { cn } from '@/shared/lib';
import { parseMarkdownBlocks } from '@/modules/assistant/parts/markdown-blocks';
import { renderInline } from '@/modules/assistant/parts/markdown-inline';
import {
  markdownClasses,
  markdownCodeBlockClasses,
  markdownCodeTextClasses,
  markdownHeadingClasses,
  markdownListClasses,
  markdownListItemClasses,
  markdownParagraphClasses,
  markdownRuleClasses,
  markdownTableCellClasses,
  markdownTableClasses,
  markdownTableHeaderCellClasses,
  markdownTableWrapperClasses,
} from '@/modules/assistant/parts/styles';
import type { MarkdownBlock } from '@/modules/assistant/parts/markdown-types';

export type MarkdownProps = {
  /** The source text. It is rendered as elements, never as markup. */
  text: string;
  className?: string;
};

/**
 * Renders a model's Markdown answer.
 *
 * The component owns no parsing state: it parses on each render and maps the
 * blocks to elements. The answer is short, the parse is linear, and a cached
 * parse would need a cache whose invalidation nobody could reason about.
 */
export function Markdown({ text, className }: MarkdownProps) {
  const blocks = parseMarkdownBlocks(text);

  return (
    <div className={cn(markdownClasses, className)}>
      {blocks.map((block, index) => (
        <MarkdownBlockView key={index} block={block} />
      ))}
    </div>
  );
}

type MarkdownBlockViewProps = { block: MarkdownBlock };

/** One block, rendered. */
function MarkdownBlockView({ block }: MarkdownBlockViewProps) {
  switch (block.kind) {
    case 'paragraph':
      return <p className={markdownParagraphClasses}>{renderInline(block.text)}</p>;
    case 'heading':
      return <MarkdownHeading level={block.level}>{renderInline(block.text)}</MarkdownHeading>;
    case 'code':
      return <MarkdownCode language={block.language} text={block.text} />;
    case 'list':
      return <MarkdownList ordered={block.ordered} items={block.items} />;
    case 'table':
      return <MarkdownTable header={block.header} rows={block.rows} />;
    case 'rule':
      return <hr className={markdownRuleClasses} />;
    default:
      return null;
  }
}

type MarkdownHeadingProps = { level: 1 | 2 | 3; children: ReactNode };

/** A heading. The level is the model's, so it is bounded to the three we render. */
function MarkdownHeading({ level, children }: MarkdownHeadingProps) {
  if (level === 1) return <h1 className={markdownHeadingClasses}>{children}</h1>;
  if (level === 2) return <h2 className={markdownHeadingClasses}>{children}</h2>;
  return <h3 className={markdownHeadingClasses}>{children}</h3>;
}

type MarkdownCodeProps = { language: string | null; text: string };

/** A fenced code block. Its text is shown exactly as the model wrote it. */
function MarkdownCode({ language, text }: MarkdownCodeProps) {
  return (
    <pre className={markdownCodeBlockClasses} data-language={language ?? undefined}>
      <code className={markdownCodeTextClasses}>{text}</code>
    </pre>
  );
}

type MarkdownListProps = { ordered: boolean; items: string[] };

/** A bullet or numbered list. */
function MarkdownList({ ordered, items }: MarkdownListProps) {
  const children = items.map((item, index) => (
    <li key={index} className={markdownListItemClasses}>
      {renderInline(item)}
    </li>
  ));

  return ordered ? (
    <ol className={markdownListClasses}>{children}</ol>
  ) : (
    <ul className={markdownListClasses}>{children}</ul>
  );
}

type MarkdownTableProps = { header: string[]; rows: string[][] };

/** A GFM pipe table. */
function MarkdownTable({ header, rows }: MarkdownTableProps) {
  return (
    <div className={markdownTableWrapperClasses}>
      <table className={markdownTableClasses}>
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th key={index} scope="col" className={markdownTableHeaderCellClasses}>
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={markdownTableCellClasses}>
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
